import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { getMissingEapCertificates } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/service/eap-settings.service';
import { importEapSettings } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/service/eap-settings-rest.service';
import { settingsModulePaths } from 'core/routes/routes';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EapSettingsImportFlowData } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-import-modal/settings-devices-eap-settings-import-modal.model';
import { MissingCertificateUploadStatus, useEapImportUpload } from '../use-eap-import-upload.hook';
import { EMPTY_MISSING_CERTIFICATES, INITIAL_MAPPINGS } from '../settings-devices-eap-settings-importing.constants';
import {
    buildImportPayloadFromState,
    getCertFilenamesForFieldFromContent,
} from '../settings-devices-eap-settings-importing.helpers';
import {
    EapImportFieldKey,
    EapImportMappings,
    MissingCertificatesByType,
} from '../settings-devices-eap-settings-importing.model';

export const useEapSettingsImporting = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const importFlowData = location.state as EapSettingsImportFlowData | undefined;

    const [selectedDevice, setSelectedDevice] = useState<string>('');
    const [mappings, setMappings] = useState<EapImportMappings>({ ...INITIAL_MAPPINGS });
    const [isMissingCertificatesModalOpen, setIsMissingCertificatesModalOpen] = useState<boolean>(false);
    const [isAbortUploadModalOpen, setIsAbortUploadModalOpen] = useState<boolean>(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [missingCertificates, setMissingCertificates] = useState<string[]>([]);
    const [missingCertificatesByType, setMissingCertificatesByType] =
        useState<MissingCertificatesByType>(EMPTY_MISSING_CERTIFICATES);
    const [missingCertificateUploadStatus, setMissingCertificateUploadStatus] =
        useState<MissingCertificateUploadStatus>('idle');

    const { files, onLoadFile, onDismissFile, resetFiles } = useEapImportUpload({
        missingCertificates,
        t,
        setMissingCertificateUploadStatus,
    });

    useEffect(() => {
        if (!importFlowData?.fileContent) {
            navigate(settingsModulePaths.eapSettings, { replace: true });
        }
    }, [importFlowData, navigate]);

    const updateMapping = (field: EapImportFieldKey, data: Partial<EapImportMappings[EapImportFieldKey]>) => {
        setMappings((previous) => ({
            ...previous,
            [field]: {
                ...previous[field],
                ...data,
            },
        }));
    };

    const resetMissingCertificatesFlow = () => {
        setMissingCertificateUploadStatus('idle');
        setIsAbortUploadModalOpen(false);
        setMissingCertificates([]);
        setMissingCertificatesByType(EMPTY_MISSING_CERTIFICATES);
        resetFiles();
    };

    const buildPayload = () =>
        buildImportPayloadFromState({
            importFlowData,
            mappings,
            selectedDevice,
            files: files.map((f) => ({ name: f.name, file: f.file })),
            missingCertificatesByType,
        });

    const submitImport = async () => {
        const payload = buildPayload();
        await importEapSettings(payload);
        updateQuery(settingsKeys.eapSettings());
        setIsMissingCertificatesModalOpen(false);
        showSuccessNotification(
            t('settings.devices.eap-settings.eap-config-list.action.import.importing.notification.success')
        );
        navigate(settingsModulePaths.eapSettings);
    };

    const onImport = async () => {
        resetMissingCertificatesFlow();
        setIsImporting(true);
        try {
            const certsInput = {
                caCertificateFilenames: getCertFilenamesForFieldFromContent({
                    importFlowData,
                    mappings,
                    fieldKey: 'caCertificateFilename',
                }),
                clientCertificateFilenames: getCertFilenamesForFieldFromContent({
                    importFlowData,
                    mappings,
                    fieldKey: 'clientCertificateFilename',
                }),
                privateKeyFilenames: getCertFilenamesForFieldFromContent({
                    importFlowData,
                    mappings,
                    fieldKey: 'clientPrivateKeyFilename',
                }),
            };

            const hasCertsToCheck =
                certsInput.caCertificateFilenames.length > 0 ||
                certsInput.clientCertificateFilenames.length > 0 ||
                certsInput.privateKeyFilenames.length > 0;

            if (!hasCertsToCheck) {
                await submitImport();
                return;
            }

            const missingResponse = await getMissingEapCertificates(certsInput);
            const allMissing = [
                ...missingResponse.caCertificateFilenames,
                ...missingResponse.clientCertificateFilenames,
                ...missingResponse.privateKeyFilenames,
            ];

            if (allMissing.length === 0) {
                setMissingCertificatesByType(missingResponse);
                await submitImport();
                return;
            }

            setMissingCertificatesByType(missingResponse);
            setMissingCertificates(allMissing);
            setIsMissingCertificatesModalOpen(true);
        } catch {
            showErrorNotification(
                t('settings.devices.eap-settings.eap-config-list.action.import.importing.notification.error')
            );
        } finally {
            setIsImporting(false);
        }
    };

    const onContinue = async () => {
        setIsImporting(true);
        try {
            await submitImport();
        } catch {
            showErrorNotification(
                t('settings.devices.eap-settings.eap-config-list.action.import.importing.notification.submit-error')
            );
        } finally {
            setIsImporting(false);
        }
    };

    const onClose = () => {
        navigate(settingsModulePaths.eapSettings);
    };

    const onAbortUpload = () => {
        resetMissingCertificatesFlow();
        setIsMissingCertificatesModalOpen(false);
    };

    return {
        t,
        importFlowData,
        selectedDevice,
        setSelectedDevice,
        mappings,
        updateMapping,
        isImporting,
        isMissingCertificatesModalOpen,
        isAbortUploadModalOpen,
        setIsAbortUploadModalOpen,
        isConfirmationModalOpen,
        setIsConfirmationModalOpen,
        missingCertificates,
        missingCertificatesByType,
        missingCertificateUploadStatus,
        files,
        onLoadFile,
        onDismissFile,
        onImport,
        onContinue,
        onClose,
        onAbortUpload,
        resetMissingCertificatesFlow,
        setIsMissingCertificatesModalOpen,
    };
};
